/**
 * The DESKTOP shell's audio (Tauri): whether we run in it, what it may capture, and the sources it offers.
 * The captures themselves are sources like any other (audioSession.captureNative / startCast).
 */

// ── NATIVE LOOPBACK (desktop only) — captures system audio with NO mic prompt, via the Tauri
// Rust WASAPI capture streamed over a localhost WebSocket into an AudioWorklet → the analyser.
// Matches SYQEL Desktop's loopback; replaces the browser getUserMedia path when running in Tauri.
export function isNativeAudio(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

/** Source family, derived from the id prefix — lets the UI group the dropdown. */
export type NativeSourceKind = 'system' | 'device' | 'app'

export interface NativeSource {
  id: string
  name: string
  isDefault: boolean
  kind: NativeSourceKind
}

function sourceKind(id: string): NativeSourceKind {
  if (id.startsWith('cap:')) return 'device'
  if (id.startsWith('pid:')) return 'app'
  return 'system'
}

/** Whether this machine will let us capture: 'granted' | 'needed' | 'n/a' (no such gate here). */
export type AudioPermission = 'granted' | 'needed' | 'n/a'

/**
 * Ask the OS what it will actually allow. macOS hides every per-app source behind Screen Recording,
 * so without this the picker just looks short and the user concludes we cannot see their music app.
 * Note the grant applies to a PROCESS: granting it does nothing until the app is reopened, which is
 * why the UI has to say so rather than silently re-listing.
 */
export async function audioPermission(): Promise<AudioPermission> {
  if (!isNativeAudio()) return 'n/a'
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return await invoke<AudioPermission>('audio_permission')
  } catch {
    return 'n/a'
  }
}

/** Show the system prompt (first time only) and report whether THIS process may capture. */
export async function requestAudioPermission(): Promise<boolean> {
  if (!isNativeAudio()) return true
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return await invoke<boolean>('audio_request_permission')
  } catch {
    return false
  }
}

export async function listNativeSources(): Promise<NativeSource[]> {
  if (!isNativeAudio()) return []
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    const raw = await invoke<{ id: string; name: string; is_default: boolean }[]>('audio_list_sources')
    return raw.map((s) => ({ id: s.id, name: s.name, isDefault: s.is_default, kind: sourceKind(s.id) }))
  } catch {
    return []
  }
}
